// App entry point. Mounts React, wires TanStack Query with the persister,
// boots the Supabase realtime subscriptions, and wires up Capacitor plugins
// when running inside the native shell.
//
// IMPORTANT: this file is the single most fragile place in the boot path.
// If anything here throws BEFORE ReactDOM.createRoot().render() runs, the
// user sees a blank WebView (which is exactly how Apple rejected v1.0.0).
// Every side effect outside the React tree is wrapped in try/catch, and
// the React render is wrapped in a top-level error boundary so a render
// exception cannot produce a blank screen either.

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient, setupPersister } from './lib/queryClient.js';
import { App } from './App.jsx';
import { sfx } from './lib/sfx.js';
import { applyToDocument as applyTheme } from './lib/themeStore.js';
import { RootErrorBoundary } from './components/RootErrorBoundary.jsx';
import { isNative, isIOS, isDesktop } from './lib/platform.js';
import './index.css';

// Apply the stored theme before React mounts so the first paint is already
// correct — no flash of the default look while components hydrate. Wrapped
// in try/catch so a corrupted localStorage entry can't prevent boot.
try { applyTheme(); } catch (err) { console.error('[boot] applyTheme failed', err); }

// Global button click sound. Delegated listener so every <button> in the app
// plays a subtle click without touching individual handlers.
try {
  if (typeof window !== 'undefined') {
    window.addEventListener('pointerdown', (e) => {
      try {
        const btn = e.target?.closest?.('button');
        if (!btn) return;
        if (btn.dataset?.silent === 'true') return;
        if (btn.disabled) return;
        sfx.click && sfx.click();
      } catch {}
    }, { passive: true });
  }
} catch (err) { console.error('[boot] sfx wiring failed', err); }

// Native-shell detection — add body classes so CSS can switch to edge-to-edge
// mode and respect safe-area insets. The `.capacitor` class is kept for
// compatibility with the existing stylesheet rules; `.desktop` is added when
// running inside the Electron shell so we can later style differently for
// the wider window (e.g. desktop-only menu bars, keyboard-shortcut hints).
// Wrapped in try/catch because a failure here on iOS used to leave the page
// with the iPhone-sized .phone frame on iPad — Apple rejected v1.0.0 over
// exactly that. The CSS @supports fallback handles wider screens regardless.
try {
  if (isNative()) {
    document.documentElement.classList.add('capacitor');
    document.body.classList.add('capacitor');
  }
  if (isDesktop()) {
    document.documentElement.classList.add('desktop');
    document.body.classList.add('desktop');
  }
} catch (err) { console.error('[boot] platform detect failed', err); }

// iOS-only: hide the Capacitor splash and configure the status bar. We
// stash the module specifiers in variables so Vite's import-analysis plugin
// can't see them at parse time — `/* @vite-ignore */` alone is supposed to
// make dynamic imports opaque, but the dev-mode analyzer ignores it for
// string-literal specifiers and tries to resolve them anyway, which breaks
// the dev server because `@capacitor/*` isn't installed in this project.
// The variable indirection is the standard escape hatch.
(async () => {
  if (!isIOS()) return;
  const splashMod = '@capacitor/splash-screen';
  const statusMod = '@capacitor/status-bar';
  try {
    const { SplashScreen } = await import(/* @vite-ignore */ splashMod);
    await SplashScreen.hide().catch(() => {});
  } catch {}
  try {
    const { StatusBar, Style } = await import(/* @vite-ignore */ statusMod);
    await StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
    await StatusBar.setBackgroundColor({ color: '#0a0d12' }).catch(() => {});
  } catch {}
})();

// Hydrate TanStack Query from localStorage so the app opens instantly with
// cached data even before the network has a chance to respond.
try { setupPersister(); } catch (err) { console.error('[boot] persister failed', err); }

// Mount React. If this throws, we still want SOMETHING on screen rather
// than a blank WebView, so the inner tree is wrapped in RootErrorBoundary
// and this whole call is wrapped in try/catch with a DOM-level fallback.
try {
  const rootEl = document.getElementById('root');
  if (!rootEl) {
    throw new Error('No #root element found in index.html');
  }
  ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
      <RootErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </RootErrorBoundary>
    </React.StrictMode>
  );
} catch (err) {
  console.error('[boot] React mount failed', err);
  try {
    const rootEl = document.getElementById('root') || document.body;
    rootEl.innerHTML =
      '<div style="position:fixed;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;background:#0a0d12;color:#eaecef;font-family:-apple-system,BlinkMacSystemFont,sans-serif;padding:24px;text-align:center"><div style="font-size:28px;font-weight:300;letter-spacing:0.02em;margin-bottom:16px">Consistency</div><div style="font-size:15px;opacity:0.75;max-width:320px;line-height:1.5">Something went wrong while loading. Try reopening the app.</div></div>';
  } catch {}
}
